import { ZodType, ZodTypeDef } from "zod";
import {
  OptroImportItem,
  OptroImportItemStatus,
  OptroImportJob,
  OptroImportListingItem,
  OptroImportListingJob,
} from "../models/audit/optroSync";

// Shared by the controls and work steps Optro import trackers
// (useOptroImportJobs): how listed and locally-started imports combine, which
// one the progress card shows, and which the user has dismissed.

export type OptroImportKind = "control" | "work_step";

// Prefix of the id an import carries until its start POST returns one.
export const PENDING_IMPORT_PREFIX = "pending:";

// Whether an item did not import: failed, timed out or cancelled. Summaries
// show the three apart (unimportedSummaryParts); this groups them for
// callers that only need to know the item did not import.
export const isFailedImportStatus = (status: OptroImportItemStatus): boolean =>
  status === "failed" || status === "timed_out" || status === "cancelled";

type UnimportedStatus = "failed" | "timed_out" | "cancelled";

const UNIMPORTED_STATUSES: UnimportedStatus[] = [
  "failed",
  "timed_out",
  "cancelled",
];

const UNIMPORTED_STATUS_LABEL: Record<UnimportedStatus, string> = {
  failed: "Failed",
  timed_out: "Timed out",
  cancelled: "Cancelled",
};

const UNIMPORTED_STATUS_SUMMARY: Record<UnimportedStatus, string> = {
  failed: "failed",
  timed_out: "timed out",
  cancelled: "cancelled",
};

// The summary parts for items that did not import, split by outcome and
// omitting zero counts: ["1 failed", "1 timed out", "1 cancelled"].
export const unimportedSummaryParts = (items: OptroImportItem[]): string[] =>
  UNIMPORTED_STATUSES.flatMap((status) => {
    const count = items.filter((item) => item.status === status).length;
    return count > 0 ? [`${count} ${UNIMPORTED_STATUS_SUMMARY[status]}`] : [];
  });

interface UnimportedItemLine {
  ref: string;
  name: string;
  // The item's failure reason, else its status label.
  detail: string;
}

// One line per item that failed, timed out or was cancelled.
export const unimportedItemLines = (
  items: OptroImportItem[],
): UnimportedItemLine[] =>
  items.flatMap((item) =>
    item.status === "failed" ||
    item.status === "timed_out" ||
    item.status === "cancelled"
      ? [
          {
            ref: item.controlExternalRef,
            name: item.controlName,
            detail: item.failureReason || UNIMPORTED_STATUS_LABEL[item.status],
          },
        ]
      : [],
  );

export interface TrackedOptroImport<TSummary> {
  id: string;
  // Identifies this import's progress card for the life of the tab. Unlike
  // `id` it does not change when a pending import gets its server id, so the
  // card keeps its collapsed and full-view state across that swap.
  instanceKey: string;
  createdAt: string;
  job: OptroImportJob;
  // Display summaries of the import's items, for placeholder rows.
  summaries: TSummary[];
  // Known only to this tab: its start POST is in flight, or the listing
  // hasn't reported it (failing, unavailable, or answered before the import
  // was recorded). Once it has a server id, until a listing reports it, its
  // status comes from the per-import endpoint; the first full listing, or
  // whole job in a delta, that reports it replaces this copy. Partial delta
  // rows merge into it, leave it local-only and force a full load.
  localOnly: boolean;
}

export const isPendingImportId = (id: string): boolean =>
  id.startsWith(PENDING_IMPORT_PREFIX);

export const toImportItem = (
  item: OptroImportListingItem,
): OptroImportItem => ({
  controlExternalRef: item.controlExternalRef,
  controlName: item.controlName,
  status: item.status,
  stage: item.stage,
  failureReason: item.failureReason,
});

// Whether every item has stopped: none is still queued or processing. An
// import with no items is not done (the backend's `is_done` agrees).
export const isImportDone = (items: OptroImportItem[]): boolean =>
  items.length > 0 &&
  items.every(
    (item) => item.status !== "queued" && item.status !== "processing",
  );

// Takes whole listed jobs only (every item of the import, and a boolean
// `isDone` from the server), never a delta's partial job: completion cannot
// be derived from a few changed rows. The type allows a null `isDone` (the
// partial case); treat it as not done.
export const trackedFromListing = <TSummary>(
  listed: OptroImportListingJob,
  summarySchema: ZodType<TSummary, ZodTypeDef, unknown>,
): TrackedOptroImport<TSummary> => {
  const items = listed.items.map(toImportItem);
  return {
    id: listed.id,
    instanceKey: listed.id,
    createdAt: listed.createdAt,
    job: {
      id: listed.id,
      isDone: listed.isDone === true,
      items,
    },
    summaries: listed.items.flatMap((item) => {
      const parsed = summarySchema.safeParse(item.display);
      return parsed.success ? [parsed.data] : [];
    }),
    localOnly: false,
  };
};

export const newestFirst = <TSummary>(
  a: TrackedOptroImport<TSummary>,
  b: TrackedOptroImport<TSummary>,
): number => Date.parse(b.createdAt) - Date.parse(a.createdAt);

// A server copy of an import this tab already tracks keeps the tracked copy's
// instance key, and when it is missing some display summaries, the tracked
// ones.
export const adoptServerCopy = <TSummary>(
  known: TrackedOptroImport<TSummary>,
  fromServer: TrackedOptroImport<TSummary>,
): TrackedOptroImport<TSummary> => ({
  ...fromServer,
  instanceKey: known.instanceKey,
  summaries:
    fromServer.summaries.length < known.summaries.length
      ? known.summaries
      : fromServer.summaries,
});

// Server copies replace local ones (see adoptServerCopy). Local-only imports
// the server doesn't list yet are kept. Newest first.
export const mergeListedImports = <TSummary>(
  current: TrackedOptroImport<TSummary>[],
  listed: TrackedOptroImport<TSummary>[],
): TrackedOptroImport<TSummary>[] => {
  const currentById = new Map(current.map((tracked) => [tracked.id, tracked]));
  const listedIds = new Set(listed.map((tracked) => tracked.id));
  const fromServer = listed.map((tracked) => {
    const known = currentById.get(tracked.id);
    return known ? adoptServerCopy(known, tracked) : tracked;
  });
  const keptLocal = current.filter(
    (tracked) => tracked.localOnly && !listedIds.has(tracked.id),
  );
  return [...keptLocal, ...fromServer].sort(newestFirst);
};

// Apply a per-import status response to a local-only import. Items the
// server lists replace local ones by ref. Once the server reports the import
// done, local items it never listed are dropped: they failed to start. The
// server's `isDone` is authoritative for completion.
export const applyStatusToJob = (
  job: OptroImportJob,
  status: OptroImportJob,
): OptroImportJob => {
  const byRef = new Map(
    status.items.map((item) => [item.controlExternalRef, item]),
  );
  const items = job.items.flatMap((item) => {
    const reported = byRef.get(item.controlExternalRef);
    if (reported) return [reported];
    return status.isDone ? [] : [item];
  });
  return {
    id: status.id,
    isDone: status.isDone,
    items,
  };
};

// Ids that were running in `previous` and are done in `next`.
export const newlyFinishedImportIds = <TSummary>(
  previous: TrackedOptroImport<TSummary>[],
  next: TrackedOptroImport<TSummary>[],
): string[] => {
  const wasRunning = new Set(
    previous
      .filter((tracked) => !tracked.job.isDone)
      .map((tracked) => tracked.id),
  );
  return next
    .filter((tracked) => tracked.job.isDone && wasRunning.has(tracked.id))
    .map((tracked) => tracked.id);
};

// The import the progress card shows: the one explicitly opened, else the
// newest one the user hasn't dismissed that is running, or that this tab saw
// running. A finished import from before this visit isn't pushed at the user;
// it stays reachable from Background Tasks.
export const pickActiveImport = <TSummary>(
  jobs: TrackedOptroImport<TSummary>[],
  openedId: string | null,
  dismissedIds: ReadonlySet<string>,
  seenRunningIds: ReadonlySet<string>,
): TrackedOptroImport<TSummary> | null => {
  if (openedId !== null) {
    const opened = jobs.find((tracked) => tracked.id === openedId);
    if (opened) return opened;
  }
  return (
    jobs.find(
      (tracked) =>
        !dismissedIds.has(tracked.id) &&
        (!tracked.job.isDone || seenRunningIds.has(tracked.id)),
    ) ?? null
  );
};

// Every tracked import's items and summaries as one job for the placeholder
// rows. The newest import wins for a ref that appears in more than one.
export const mergeImportsForPlaceholders = <
  TSummary extends { externalRef: string },
>(
  jobs: TrackedOptroImport<TSummary>[],
): { job: OptroImportJob | null; summaries: TSummary[] } => {
  if (jobs.length === 0) return { job: null, summaries: [] };
  const itemsByRef = new Map<string, OptroImportItem>();
  const summariesByRef = new Map<string, TSummary>();
  for (const tracked of [...jobs].reverse()) {
    for (const item of tracked.job.items) {
      itemsByRef.set(item.controlExternalRef, item);
    }
    for (const summary of tracked.summaries) {
      summariesByRef.set(summary.externalRef, summary);
    }
  }
  return {
    job: {
      id: "tracked-imports",
      isDone: jobs.every((tracked) => tracked.job.isDone),
      items: [...itemsByRef.values()],
    },
    summaries: [...summariesByRef.values()],
  };
};

const dismissalPrefix = (kind: OptroImportKind) =>
  `optroImportCardDismissed:${kind}:`;

// Import ids whose card the user closed in this tab (sessionStorage, like
// the document-processing toast). Storage can be unavailable; then nothing
// is remembered.
export const readDismissedImportIds = (kind: OptroImportKind): Set<string> => {
  const prefix = dismissalPrefix(kind);
  try {
    const ids = new Set<string>();
    for (let index = 0; index < sessionStorage.length; index += 1) {
      const key = sessionStorage.key(index);
      if (key?.startsWith(prefix)) ids.add(key.slice(prefix.length));
    }
    return ids;
  } catch {
    return new Set();
  }
};

export const writeImportDismissed = (
  kind: OptroImportKind,
  id: string,
  dismissed: boolean,
): void => {
  const key = `${dismissalPrefix(kind)}${id}`;
  try {
    if (dismissed) sessionStorage.setItem(key, "1");
    else sessionStorage.removeItem(key);
  } catch {
    // Storage unavailable (private mode, quota): the dismissal lasts this render tree only.
  }
};
